import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../common/Button";
import { updateInterventionAssessment } from "../../../../../redux/modules/intervention/actions";
import CustomInput from "../../../../common/CustomInput";
import PharmacistTipTapEditor from "../../../../common/PharmacistTipTapEditor";
import axios from "axios";
import {
  WhenOptions,
  WhoOptions,
  WhyOptions,
  OverrideReasonOptions,
  AmrDisplayKind,
  formatAmrForDisplay,
} from "../../../clinical-services/service-steps/medication/data";
import { DPTOptions } from "../../../text/data";
import { findAvailableStep } from "../../../text/data";
import NewToolTip from "../../../../common/New-ToolTip";
import { useMonographStatuses } from "../../../../../hooks/useMonographStatuses";
import {
  isAnnualMedicationReviewIntervention,
  getAmrMedicationRowForIntervention,
} from "../data";
import { capitalizeFirstLetter } from "../../../../../helpers/functions";

// DPTOptions now imported from shared text/data

const actionPlanOptions = [
  { value: "Does not apply", label: "Does not apply" },
  { value: "Adapt Prescription", label: "Adapt Prescription" },
  { value: "Change/ Start/ Stop", label: "Change/ Start/ Stop" },
  { value: "Contact Prescriber", label: "Contact Prescriber" },
  { value: "Eliminate Patient Barriers", label: "Eliminate Patient Barriers" },
  { value: "Give Immunization", label: "Give Immunization" },
  { value: "Initiate Drug - Disease Monitoring", label: "Initiate Drug - Disease Monitoring" },
  { value: "Initiate Drug Therapy Monitoring", label: "Initiate Drug Therapy Monitoring" },
  { value: "Initiate Monitoring for Medical Condition", label: "Initiate Monitoring for Medical Condition" },
  { value: "Provide Education", label: "Provide Education" },
  { value: "Referral to Other", label: "Referral to Other" },
  { value: "Referral to Physician", label: "Referral to Physician" },
  { value: "Other (specify)", label: "Other (specify)" },
];

const OTHER_ACTION_PLAN_VALUE = "Other (specify)";
const DOES_NOT_APPLY_ACTION_PLAN_VALUE = "Does not apply";

// Use the same options as interventionAssessment.jsx
const CLINICAL_DECISION_OPTIONS = OverrideReasonOptions;

/** Checkbox control for saving comments to documentation form. */
const SaveCommentsToDocumentationRadio = ({
  id,
  checked,
  onToggle,
  embedded = false,
}) => (
  <div
    className={`flex items-start gap-2 ${
      embedded
        ? "rounded-sm bg-white/95 pr-1"
        : ""
    }`}
  >
    <input
      type="checkbox"
      id={id}
      checked={checked}
      aria-label="Click to Save Comments on Documentation Form"
      onChange={onToggle}
      className="-mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-sm border-2 border-gray-300 text-teal-600 focus:ring-2 focus:ring-REELTeal focus:ring-offset-1 [&::-webkit-appearance]:none [&::-moz-appearance]:none"
    />
    <label
      htmlFor={id}
      className="cursor-pointer select-none text-left text-sm leading-snug text-gray-800"
    >
      Select if wish to ADD Pharmacist Comments to documentation form 
    </label>
  </div>
);

const RxVigilanceDinSearch = ({ din }) => {
  const { data: monographStatusByIdentifier } = useMonographStatuses([din]);

  const vigilanceEnv = import.meta.env.VITE_VIGILANCE_ENV || "int";
  const rxVigilanceBaseUrl = `https://rx.${vigilanceEnv}.vigilance.ca`;
  const rxVigilanceAuthUrl = `https://rxauth.${vigilanceEnv}.vigilance.ca`;

  const monographStatus =
    din != null ? monographStatusByIdentifier[String(din)] : null;
  const hasMonograph = monographStatus?.hasMonograph;
  const monographId = monographStatus?.monographId;

  const handleClick = async () => {
    if (!hasMonograph || !monographId) {
      return;
    }

    try {
      const tokenResponse = await axios.post(
        `${rxVigilanceAuthUrl}/token`,
        new URLSearchParams({
          client_id: import.meta.env.VITE_APP_VIGILANCE_ID,
          client_secret: import.meta.env.VITE_APP_VIGILANCE_SECRET,
          user_id: "REEL01",
          grant_type: "client_credentials",
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      const token = tokenResponse.data.access_token;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${rxVigilanceBaseUrl}/module/mono/en/${monographId}.htm`;
      form.target = "_blank";

      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "token";
      tokenInput.value = token;

      form.appendChild(tokenInput);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error("Failed to open monograph:", error);
    }
  };

  const tooltipId = `monograph-${din}`;

  return (
    <>
      <img
        src="/icons/rx-vigilance-icon.svg"
        alt="DIN Monograph"
        data-tooltip-id={tooltipId}
        className="cursor-pointer w-10 min-w-[40px]"
        onClick={handleClick}
      />
      {(!din || (monographStatus && !hasMonograph)) && (
        <NewToolTip
          id={tooltipId}
          place="bottom"
          render={() => (
            <div className="flex flex-col max-w-md p-2 bg-gray-20">
              <p className="text-sm text-teal-600">No monograph available</p>
            </div>
          )}
        />
      )}
    </>
  );
};

const InterventionDetails = ({ intervention, currentInterventionSteps, currentStep: activeStep = 1 }) => {
  const dispatch = useDispatch();
  const { interventionId } = useParams();
  const clinicalServiceObj = useSelector((state) => state.intervention.clinicalServiceObj);
  const { medications } = useSelector((state) => state.clinicalService);

  const amrMedicationRow = useMemo(() => {
    if (!intervention || !isAnnualMedicationReviewIntervention(intervention)) {
      return null;
    }
    return getAmrMedicationRowForIntervention(clinicalServiceObj, intervention);
  }, [clinicalServiceObj, intervention]);

  // Rx-Vigilance uses the medication DIN/identifier.
  // For AMR interventions: resolved from the AMR medication row.
  // For non-AMR interventions: resolved via medicationId stored on the intervention form,
  // looked up in the patient medications list (same approach as PrescriberFollowUp.jsx).
  const dinForMonograph = useMemo(() => {
    const amrDin =
      amrMedicationRow?.medicationGenCode ??
      amrMedicationRow?.MedicationGenCode ??
      amrMedicationRow?.din ??
      amrMedicationRow?.DIN ??
      null;
    if (amrDin != null) return amrDin;

    const medicationIdForMonograph =
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.interventionDetails?.medicationId;
    if (!medicationIdForMonograph || !Array.isArray(medications) || medications.length === 0) {
      return null;
    }
    const med = medications.find((m) => m.id === medicationIdForMonograph);
    return (
      med?.medicationGenCode ??
      med?.MedicationGenCode ??
      med?.din ??
      med?.DIN ??
      null
    );
  }, [amrMedicationRow, intervention, medications]);

  const amrAdherenceForAssessmentHeading =
    amrMedicationRow?.adherence ??
    amrMedicationRow?.Adherence ??
    amrMedicationRow?.consumption ??
    amrMedicationRow?.Consumption ??
    null;

  /** Free-text comments from the AMR medication review card (`medications.medications[].comments`). */
  const amrCommentsFromReview = useMemo(() => {
    if (!amrMedicationRow) return "";
    const raw =
      amrMedicationRow.comments ?? amrMedicationRow.Comments ?? "";
    if (raw == null) return "";
    return typeof raw === "string" ? raw : String(raw);
  }, [amrMedicationRow]);

  // Local state for editable fields - matching interventionCreate.jsx
  const [DTPValue, setDTPValue] = useState(null);
  const [actionPlan, setActionPlan] = useState([]);
  const [otherActionPlan, setOtherActionPlan] = useState("");
  const [drugTherapySuggestions, setDrugTherapySuggestions] = useState("");
  const [pharmacistAssessmentComments, setPharmacistAssessmentComments] =
    useState("");
  const [
    saveAssessmentCommentsToDocumentationForm,
    setSaveAssessmentCommentsToDocumentationForm,
  ] = useState(false);
  const [
    savePharmacistCommentsToDocumentationForm,
    setSavePharmacistCommentsToDocumentationForm,
  ] = useState(false);
  const prevInterventionIdRef = useRef(null);

  // Initialize local state from intervention data
  useEffect(() => {
    const details =
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.interventionDetails;
    const currentId = intervention?.id;
    const interventionRecordChanged =
      currentId != null && currentId !== prevInterventionIdRef.current;
    if (currentId != null) {
      prevInterventionIdRef.current = currentId;
    }

    if (details) {
      if (details.drugTherapyProblem) {
        setDTPValue(details.drugTherapyProblem);
      }
      if (details.actionPlan && Array.isArray(details.actionPlan)) {
        setActionPlan(details.actionPlan);
      }
      setOtherActionPlan(
        typeof details.otherActionPlan === "string"
          ? details.otherActionPlan
          : ""
      );
      const legacy = details.saveCommentsToDocumentationForm;
      const a = details.saveAssessmentCommentsToDocumentationForm;
      const p = details.savePharmacistCommentsToDocumentationForm;

      if (a === true || a === false) {
        setSaveAssessmentCommentsToDocumentationForm(a);
      } else if (legacy === true || legacy === false) {
        setSaveAssessmentCommentsToDocumentationForm(legacy);
      } else if (interventionRecordChanged) {
        setSaveAssessmentCommentsToDocumentationForm(false);
      }

      if (p === true || p === false) {
        setSavePharmacistCommentsToDocumentationForm(p);
      } else if (legacy === true || legacy === false) {
        setSavePharmacistCommentsToDocumentationForm(legacy);
      } else if (interventionRecordChanged) {
        setSavePharmacistCommentsToDocumentationForm(false);
      }
    } else if (interventionRecordChanged) {
      setSaveAssessmentCommentsToDocumentationForm(false);
      setSavePharmacistCommentsToDocumentationForm(false);
      setOtherActionPlan("");
    }

    const savedSuggestions = details?.drugTherapySuggestions;
    if (savedSuggestions != null) {
      setDrugTherapySuggestions(
        typeof savedSuggestions === "string"
          ? savedSuggestions
          : String(savedSuggestions)
      );
    } else if (amrCommentsFromReview) {
      setDrugTherapySuggestions(amrCommentsFromReview);
    } else {
      setDrugTherapySuggestions("");
    }

    const createComments =
      details?.comments ??
      details?.Comments ??
      details?.pharmacistComments ??
      details?.PharmacistComments ??
      intervention?.pharmacistComments;
    if (createComments != null) {
      setPharmacistAssessmentComments(
        typeof createComments === "string"
          ? createComments
          : String(createComments)
      );
    } else {
      setPharmacistAssessmentComments("");
    }
  }, [intervention, amrCommentsFromReview]);

  const hasOtherActionPlan =
    Array.isArray(actionPlan) && actionPlan.includes(OTHER_ACTION_PLAN_VALUE);

  const handleActionPlanChange = (selectedActionPlans) => {
    const nextActionPlan = Array.isArray(selectedActionPlans)
      ? selectedActionPlans
      : [];

    if (nextActionPlan.includes(DOES_NOT_APPLY_ACTION_PLAN_VALUE)) {
      setActionPlan([DOES_NOT_APPLY_ACTION_PLAN_VALUE]);
      setOtherActionPlan("");
      return;
    }

    setActionPlan(nextActionPlan);

    if (!nextActionPlan.includes(OTHER_ACTION_PLAN_VALUE)) {
      setOtherActionPlan("");
    }
  };

  const persistInterventionDetails = async (detailsPatch = {}) => {
    const formRoot =
      intervention?.interventionAssessment?.interventionAssessmentForm || {};
    const prevDetails = formRoot.interventionDetails || {};
    await dispatch(
      updateInterventionAssessment(interventionId, {
        currentStep: activeStep ?? formRoot.currentStep ?? 1,
        startDateTime:
          formRoot.startDateTime || new Date().toISOString(),
        interventionDetails: {
          ...prevDetails,
          drugTherapyProblem: DTPValue,
          actionPlan,
          otherActionPlan: hasOtherActionPlan ? otherActionPlan : "",
          drugTherapySuggestions,
          comments: pharmacistAssessmentComments,
          pharmacistComments: pharmacistAssessmentComments,
          saveAssessmentCommentsToDocumentationForm,
          savePharmacistCommentsToDocumentationForm,
          ...detailsPatch,
        },
      })
    );
  };

  const handleSaveDocumentationToggle = async (which) => {
    if (which === "assessment") {
      const next = !saveAssessmentCommentsToDocumentationForm;
      setSaveAssessmentCommentsToDocumentationForm(next);
      try {
        await persistInterventionDetails({
          saveAssessmentCommentsToDocumentationForm: next,
        });
      } catch {
        setSaveAssessmentCommentsToDocumentationForm(!next);
      }
      return;
    }
    const next = !savePharmacistCommentsToDocumentationForm;
    setSavePharmacistCommentsToDocumentationForm(next);
    try {
      await persistInterventionDetails({
        savePharmacistCommentsToDocumentationForm: next,
      });
    } catch {
      setSavePharmacistCommentsToDocumentationForm(!next);
    }
  };

  // Navigation functions
  const onNext = async () => {
    if (!Array.isArray(actionPlan) || actionPlan.length === 0) {
      return;
    }

    try {
      // Find next available step using common function
      const currentStep =
        activeStep ||
        intervention?.interventionAssessment?.interventionAssessmentForm
          ?.currentStep ||
        1;
      const nextStep = findAvailableStep(
        currentStep,
        currentInterventionSteps,
        1
      );

      const formRoot =
        intervention?.interventionAssessment?.interventionAssessmentForm || {};
      const nextStartDateTime =
        formRoot.startDateTime || new Date().toISOString();

      // Prepare the data to save
      const assessmentData = {
        currentStep: nextStep,
        startDateTime: nextStartDateTime,
        interventionDetails: {
          drugTherapyProblem: DTPValue, // Single value like interventionCreate.jsx
          actionPlan: actionPlan,
          otherActionPlan: hasOtherActionPlan ? otherActionPlan : "",
          drugTherapySuggestions: drugTherapySuggestions,
          comments: pharmacistAssessmentComments,
          pharmacistComments: pharmacistAssessmentComments,
          saveAssessmentCommentsToDocumentationForm,
          savePharmacistCommentsToDocumentationForm,
        },
      };

      // Save the changes and navigate to next step
      await dispatch(
        updateInterventionAssessment(interventionId, assessmentData)
      );
      // State will be updated automatically via Redux when API response comes back
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  const onPrevious = async () => {
    try {
      // Find previous available step using common function
      const currentStep =
        activeStep ||
        intervention?.interventionAssessment?.interventionAssessmentForm
          ?.currentStep ||
        1;
      const previousStep = findAvailableStep(
        currentStep,
        currentInterventionSteps,
        -1
      );

      // Prepare the data to save
      const assessmentData = {
        currentStep: previousStep,
        interventionDetails: {
          drugTherapyProblem: DTPValue,
          actionPlan: actionPlan,
          otherActionPlan: hasOtherActionPlan ? otherActionPlan : "",
          drugTherapySuggestions: drugTherapySuggestions,
          comments: pharmacistAssessmentComments,
          pharmacistComments: pharmacistAssessmentComments,
          saveAssessmentCommentsToDocumentationForm,
          savePharmacistCommentsToDocumentationForm,
        },
      };

      // Save the changes and navigate to previous step
      await dispatch(
        updateInterventionAssessment(interventionId, assessmentData)
      );
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  // Determine if buttons should be enabled
  const canGoNext = Array.isArray(actionPlan) && actionPlan.length > 0;
  const canGoPrevious = false; // This is the first step

  if (!intervention) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-400 text-xl font-medium mb-4">
            No Intervention Data
          </div>
          <div className="text-gray-500 text-base">
            Intervention details are not available.
          </div>
        </div>
      </div>
    );
  }

  // Extract intervention details from the nested structure
  const interventionDetails =
    intervention.interventionAssessment?.interventionAssessmentForm
      ?.interventionDetails || {};

  // Helper function to get medication display name
  const getMedicationDisplayName = () => {
    return (
      interventionDetails.medicationName ||
      intervention.medicationName ||
      "Name of Drug"
    );
  };

  // Helper function to get current regimen from intervention details
  const getCurrentRegimenDisplayName = () => {
    if (intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails) {
      const details = intervention.interventionAssessment.interventionAssessmentForm.interventionDetails;
      const currentRegimenName = `${details.medicationName || 'Unknown Medication'} ${details.route || ''} [${details.dosage || ''} ${details.form || ''} ${details.frequency || ''}]`.trim();
      return currentRegimenName;
    }
    
    // Fallback to medication name or default
    return getMedicationDisplayName() || "Current Regimen";
  };

  // Helper function to get clinical decision display text
  const getClinicalDecisionDisplay = () => {
    const decisionValue = interventionDetails.override;
    if (!decisionValue) return "Select clinical decision...";

    const option = CLINICAL_DECISION_OPTIONS.find(
      (opt) => opt.value === decisionValue
    );
    return option ? option.label : decisionValue;
  };

  // Helper function to get drug therapy problem display
  const getDrugTherapyProblemDisplay = () => {
    if (!interventionDetails.drugTherapyProblem) return "";
    const option = drugTherapyProblemOptions.find(
      (opt) => opt.value === interventionDetails.drugTherapyProblem
    );
    return option ? option.label : interventionDetails.drugTherapyProblem;
  };

  // Helper function to get action plan display
  const getActionPlanDisplay = () => {
    if (
      !interventionDetails.actionPlan ||
      !Array.isArray(interventionDetails.actionPlan) ||
      interventionDetails.actionPlan.length === 0
    ) {
      return "";
    }
    const firstAction = interventionDetails.actionPlan[0];
    const option = ACTION_PLAN_OPTIONS.find(
      (opt) => opt.value === firstAction || opt.label === firstAction
    );
    return option ? option.label : firstAction;
  };

  // Helper function to get when changed display
  const getWhenChangedDisplay = () => {
    if (!interventionDetails.whenChangedMedication) return "";
    const option = WhenOptions.find(
      (opt) => opt.value === interventionDetails.whenChangedMedication
    );
    return option ? option.label : interventionDetails.whenChangedMedication;
  };

  // Helper function to get who changed display
  const getWhoChangedDisplay = () => {
    if (!interventionDetails.whoChangedMedication) return "";
    const option = WhoOptions.find(
      (opt) => opt.value === interventionDetails.whoChangedMedication
    );
    return option ? option.label : interventionDetails.whoChangedMedication;
  };

  // Helper function to get why changed display
  const getWhyChangedDisplay = () => {
    if (!interventionDetails.whyChangedMedication) return "";
    const option = WhyOptions.find(
      (opt) => opt.value === interventionDetails.whyChangedMedication
    );
    return option ? option.label : interventionDetails.whyChangedMedication;
  };

  return (
    <>
      <div className="w-full max-h-fit bg-white overflow-x-hidden overflow-auto">
        <div className="w-full h-full flex flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-8 py-3 min-w-0 xl:flex-row">
            <div className="flex w-full min-w-0 min-h-[610px] flex-col rounded-[4px] border border-gray-100 bg-gray-10 xl:flex-[0.88]">
              {/* header */}
              <div className="flex h-[72px] shrink-0 justify-between items-center border-b border-gray-200 px-6 py-3">
                <div className="text-teal-300 font-semibold text-xl">
                  Assessment Results
                </div>
                <div className="text-teal-300 cursor-pointer"></div>
              </div>
              {/* content — grows with short content; scrollbar only when taller than max */}
              <div className="max-h-[538px] overflow-x-hidden overflow-y-auto overscroll-contain px-6 pt-[14px] pb-9">
                <div className="flex flex-col gap-[8px] pb-2">
                  <div
                    className={`font-bold text-gray-800 mb-2 ${
                      !isAnnualMedicationReviewIntervention(intervention) ? "mt-4" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-600 mb-1">
                          Original Prescriptions
                        </p>
                        {getMedicationDisplayName()}
                      </div>
                      <div className="flex-shrink-0">
                        <RxVigilanceDinSearch din={dinForMonograph} />
                      </div>
                    </div>
                  </div>
                  <div
                    className={`mb-3 ${
                      !isAnnualMedicationReviewIntervention(intervention) ? "mt-2" : ""
                    }`}
                  >
                    <div
                      className={`w-full min-w-0 h-[70px] gap-[4px] flex flex-col rounded-md ${
                        DTPValue === null ? "bg-blue-100" : "bg-white"
                      }`}
                    >
                      <CustomInput
                        options={DPTOptions}
                        name="DPTOptions"
                        className={`w-full !text-sm !font-normal ${
                          DTPValue === null ? "!bg-blue-100" : "bg-white"
                        }`}
                        label="Drug Therapy Problem"
                        labelClassName="!text-sm"
                        type="dropdown"
                        value={DTPValue}
                        onChange={(e) => setDTPValue(e.value)}
                        inputClassName="!text-base !text-teal-300"
                        disabled={false}
                        showOptionalLabel={false}
                      />
                    </div>
                  </div>

                  {isAnnualMedicationReviewIntervention(intervention) && (
                    <div className="border border-gray-100 rounded-md px-[13px] py-2 flex flex-col gap-[4px] bg-white">
                      <div className="text-base font-semibold text-teal-300 mb-1 border-b border-gray-200 pb-1">
                        {formatAmrForDisplay(
                          AmrDisplayKind.AssessmentStatusHeading,
                          amrAdherenceForAssessmentHeading
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-normal text-gray-700 w-[220px]">
                        Medication Effectiveness:
                        </div>
                        <div className="w-[250px] text-base text-gray-800 font-medium">
                          {formatAmrForDisplay(
                            AmrDisplayKind.CommonQuestion,
                            amrMedicationRow?.effectiveness ?? amrMedicationRow?.Effectiveness
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm font-normal text-gray-700 w-[220px]">
                          Safety/ Side Effects:
                        </div>
                        <div className="w-[250px] text-base text-gray-800 font-medium">
                          {formatAmrForDisplay(
                            AmrDisplayKind.CommonQuestion,
                            amrMedicationRow?.safety ?? amrMedicationRow?.Safety
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm font-normal text-gray-700 w-[220px]">
                          Appropriate Technique to Use:
                        </div>
                        <div className="w-[250px] text-base text-gray-800 font-medium">
                          {formatAmrForDisplay(
                            AmrDisplayKind.CommonQuestion,
                            amrMedicationRow?.properTechnique ?? amrMedicationRow?.ProperTechnique,
                            { forTechnique: true }
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {isAnnualMedicationReviewIntervention(intervention) && (
                    <div className="border border-gray-100 rounded-md px-[13px] py-2 flex flex-col gap-[4px] bg-white -mt-2 border-t-2">
                       <div className="text-base font-semibold text-teal-300 mb-1 border-b border-gray-200 pb-1">Medication Change Summary</div>
                      {/* When did the change occur? */}
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-normal text-gray-700 w-[220px]">
                          When did the change occur?
                        </div>
                        <div className="w-[250px] text-base text-gray-800 font-medium">
                          {interventionDetails.whenChangedMedication || ""}
                        </div>
                      </div>

                      {/* Date picker for specific date */}
                      {/* {interventionDetails.whenChangedMedication ===
                      "Add Specific Date" &&
                      interventionDetails.whenChangedDate && (
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-normal text-gray-700 w-[190px]">
                            Specific Date:
                          </div>
                          <div className="w-[270px] text-sm text-gray-800">
                            {new Date(
                              interventionDetails.whenChangedDate
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      )} */}

                      {/* Who initiated the change? */}
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-normal text-gray-700 w-[220px]">
                          Who initiated the change?
                        </div>
                        <div className="w-[250px] text-base text-gray-800 font-medium">
                          {capitalizeFirstLetter(interventionDetails.whoChangedMedication || "")}
                        </div>
                      </div>

                      {/* Why did the change occur? */}
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-normal text-gray-700 w-[220px]">
                          Why did the change occur?
                        </div>
                        <div className="w-[250px] text-base text-gray-800 font-medium">
                          {interventionDetails.whyChangedMedication || ""}
                        </div>
                      </div>
                    </div>
                  )}
                  {isAnnualMedicationReviewIntervention(intervention) && (
                    <div className="border border-gray-100 rounded-md px-3 flex flex-col gap-[8px] bg-white mt-2 py-2">
                      <PharmacistTipTapEditor
                        value={drugTherapySuggestions}
                        onChange={setDrugTherapySuggestions}
                        headerTitle={
                          <span className="text-sm font-semibold text-teal-300">
                            Pharmacist Medication Assessment Comments (Optional)
                          </span>
                        }
                        minHeight="4.5rem"
                        maxHeight="7.5rem"
                        hideOuterBorder
                        ariaLabel="Pharmacist assessment comments"
                      />
                      <SaveCommentsToDocumentationRadio
                        id={`save-doc-assessment-${interventionId ?? "new"}`}
                        checked={saveAssessmentCommentsToDocumentationForm}
                        onToggle={() => handleSaveDocumentationToggle("assessment")}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex w-full min-w-0 min-h-[610px] flex-col rounded-[4px] border border-gray-100 bg-gray-10 xl:flex-[1.10]">
              <div className="flex h-[72px] shrink-0 justify-between items-center border-b border-gray-200 px-6 py-3">
                <div className="text-teal-300 font-semibold text-xl">
                  Pharmacist Recommendations
                </div>
                <div className="text-teal-300 cursor-pointer"></div>
              </div>
              <div className="flex flex-col h-[538px] px-6 pt-[14px] pb-9 gap-[50px]">
                <div className="flex flex-col gap-[8px]">
                  {isAnnualMedicationReviewIntervention(intervention) && (
                    <div className="font-bold text-gray-800 mb-2">
                      <p className="text-sm font-semibold text-gray-600 mb-1">
                        Current Regimen (How the patient is taking the medication)
                      </p>
                      {getCurrentRegimenDisplayName()}
                    </div>
                  )}

                  <div
                    className={`w-full min-w-0 gap-[4px] flex flex-col mb-3 rounded-md ${
                      actionPlan.length > 0 ? "bg-white" : "bg-blue-100"
                    }`}
                  >
                    <CustomInput
                      options={actionPlanOptions}
                      name="actionPlan"
                      className="w-full"
                      value={actionPlan}
                      label="Action Plan"
                      labelClassName="!text-sm"
                      type="multi-dropdown"
                      onChange={handleActionPlanChange}
                      exclusiveSelectValues={[DOES_NOT_APPLY_ACTION_PLAN_VALUE]}
                      closeOnSelectValues={[DOES_NOT_APPLY_ACTION_PLAN_VALUE]}
                      disabled={false}
                      showOptionalLabel={false}
                    />
                  </div>

                  {hasOtherActionPlan && (
                    <CustomInput
                      name="otherActionPlan"
                      className="w-full bg-white -mt-1 mb-3"
                      label="Specify Action Plan"
                      labelClassName="!text-sm"
                      type="textarea"
                      value={otherActionPlan}
                      placeholder="Specify action plan"
                      onChange={(e) => setOtherActionPlan(e.target.value)}
                      inputClassName="!min-h-[84px] !text-base resize-y"
                      showOptionalLabel={false}
                    />
                  )}

                  <div className="flex max-h-[250px] min-h-[230px] flex-col rounded border border-gray-100 bg-white px-3 py-2 -mt-1">
                    <div className="min-h-0 flex-1 flex flex-col">
                      <PharmacistTipTapEditor
                        value={pharmacistAssessmentComments}
                        onChange={setPharmacistAssessmentComments}
                        headerTitle={
                          <span className="text-base font-semibold text-teal-300">
                            {`Pharmacist's Intervention Comments (Optional)`}
                          </span>
                        }
                        minHeight="132px"
                        maxHeight="180px"
                        className="border-0 flex-1"
                        editorWrapClassName="pb-14"
                        ariaLabel="Pharmacist intervention comments"
                      />
                      <div className="mt-auto pt-0">
                        <SaveCommentsToDocumentationRadio
                          id={`save-doc-recommendations-${interventionId ?? "new"}`}
                          checked={savePharmacistCommentsToDocumentationForm}
                          onToggle={() =>
                            handleSaveDocumentationToggle("pharmacist")
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {isAnnualMedicationReviewIntervention(intervention) && (
                    <div className="flex flex-col mt-8">
                      <CustomInput
                        name="pharmacistClinicalDecision"
                        className="w-full bg-yellow-50 border-yellow-300 !opacity-100"
                        label="Pharmacist Clinical Decision"
                        labelClassName="!text-sm text-teal-300 !font-semibold"
                        type="text"
                        value={getClinicalDecisionDisplay()}
                        onChange={() => {}}
                        disabled={true}
                        inputClassName="!text-base"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between space-x-4">
        <div className="w-full">{/* Progress indicator could go here */}</div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
            disabled={!canGoPrevious}
          />
          <Button
            type="primary"
            text="Next"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onNext}
            disabled={!canGoNext}
          />
        </div>
      </div>
    </>
  );
};

export default InterventionDetails;
